import styled from 'foundations-theming/utils';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { propertyLabelTranslator } from 'property-translator/propertyTranslator';
// @ts-expect-error untyped
import KnowledgeBaseButton from 'ui-addon-i18n/components/KnowledgeBaseButton';
import UITruncateString from 'UIComponents/text/UITruncateString';
import SortTH from '../SortTH';
import { SET_REP_OVERVIEW_TABLE_SORT } from '../../actions/ActionTypes';
import { FORECAST_CATEGORY } from '../../constants/DealGroupingType';
import { DefaultTableColumns, DEAL_GROUPING_SUFFIX } from '../../constants/OverviewTableConstants';
import Links from '../../constants/Links';
import { ForecastAmountSettingOptions } from '../../constants/PortalConfigConstants';
import { getPortalConfig } from '../../selectors/forecastSelectors';
import { getRepOverviewTableSort } from '../../selectors/sortSelectors';
import { getDealGrouping } from '../../utils/dealGroupingUtils';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const HeaderRow = styled.tr.withConfig({
  displayName: "RepOverviewTableHeaders__HeaderRow",
  componentId: "sc-1na9knm-0"
})(["display:table-row;background:", ";height:56px;"], ({
  theme
}) => theme.color['do-not-use-gypsum']);
const getDealGroupingHeaderTooltip = forecastCategory => {
  switch (forecastCategory) {
    case 'PIPELINE':
      return 'repOverviewTable.headers.tooltips.forecastCategories.small';
    case 'BEST_CASE':
      return 'repOverviewTable.headers.tooltips.forecastCategories.moderate';
    case 'COMMIT':
      return 'repOverviewTable.headers.tooltips.forecastCategories.veryLikely';
    default:
      return null;
  }
};
const getCoverageHeaderTooltip = (portalConfig, isForecastCategory) => {
  if (portalConfig === ForecastAmountSettingOptions.FORECAST_AMOUNT) {
    return isForecastCategory ? 'repOverviewTable.headers.tooltips.forecastCategoryWeightedPipeline_forecastAmount' : 'repOverviewTable.headers.tooltips.dealStageWeightedPipeline_forecastAmount';
  }
  if (portalConfig === ForecastAmountSettingOptions.WEIGHTED_AMOUNT) {
    return isForecastCategory ? 'repOverviewTable.headers.tooltips.forecastCategoryWeightedPipeline' : 'repOverviewTable.headers.tooltips.dealStageWeightedPipeline';
  }

  // Total Deal Amount
  return isForecastCategory ? 'repOverviewTable.headers.tooltips.forecastCategoryTotalPipeline' : 'repOverviewTable.headers.tooltips.dealStageTotalPipeline';
};
const RepOverviewTableHeaders = ({
  columns,
  dealRollupGroupings,
  groupingType
}) => {
  const portalConfig = useSelector(getPortalConfig);
  const sort = useSelector(getRepOverviewTableSort);
  const dispatch = useDispatch();
  const theme = useTheme();
  const setSort = useCallback(({
    columnKey,
    sortValue
  }) => {
    dispatch({
      type: SET_REP_OVERVIEW_TABLE_SORT,
      payload: {
        columnKey,
        sortValue
      }
    });
  }, [dispatch]);
  const renderNameHeader = () => {
    return (
      /*#__PURE__*/
      /* @bend-expect trellis-tokenized */
      _jsx(SortTH, {
        style: {
          position: 'sticky',
          left: 0,
          boxShadow: `inset 0 -1px 0 ${theme.color['border-primary-subtle']}`
        },
        columnKey: DefaultTableColumns.NAME,
        sort: sort,
        onSortChange: setSort,
        truncate: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "repOverviewTable.headers.name"
        })
      }, DefaultTableColumns.NAME)
    );
  };
  const renderAttainmentHeader = () => {
    return /*#__PURE__*/_jsxs(SortTH, {
      columnKey: DefaultTableColumns.ATTAINMENT,
      sort: sort,
      onSortChange: setSort,
      truncate: true,
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "repOverviewTable.headers.attainment"
      }), /*#__PURE__*/_jsx(HelpIcon, {
        marginInlineStart: "100",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "repOverviewTable.headers.tooltips.attainment"
        })
      })]
    }, DefaultTableColumns.ATTAINMENT);
  };
  const renderForecastHeader = () => {
    return (
      /*#__PURE__*/
      /* @bend-expect trellis-tokenized */
      _jsx(SortTH, {
        align: "right",
        columnKey: DefaultTableColumns.FORECAST,
        onSortChange: setSort,
        truncate: false,
        sort: sort,
        style: {
          position: 'sticky',
          right: 0,
          boxShadow: `
            inset 0 -1px 0 ${theme.color['border-primary-subtle']},
            inset -1px 0 0 ${theme.color['border-primary-subtle']}
          `
        },
        children: /*#__PURE__*/_jsxs(Flex, {
          children: [/*#__PURE__*/_jsx(UITruncateString, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "repOverviewTable.headers.forecast"
            })
          }), /*#__PURE__*/_jsx(HelpIcon, {
            marginInlineStart: "100",
            title: /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: "repOverviewTable.headers.forecastTooltip"
              }), "\xA0", /*#__PURE__*/_jsx(KnowledgeBaseButton, {
                url: Links.SUBMIT_A_FORECAST
              })]
            })
          })]
        })
      }, DefaultTableColumns.FORECAST)
    );
  };
  const renderCoverageHeader = () => {
    return /*#__PURE__*/_jsxs(SortTH, {
      align: "right"
      //Sorts on total vs ratio
      ,
      columnKey: DefaultTableColumns.COVERAGE,
      onSortChange: setSort,
      sort: sort,
      children: [/*#__PURE__*/_jsx(UITruncateString, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: portalConfig === ForecastAmountSettingOptions.WEIGHTED_AMOUNT || portalConfig === ForecastAmountSettingOptions.FORECAST_AMOUNT ? 'repOverviewTable.headers.weightedPipeline' : 'repOverviewTable.headers.totalPipeline'
        })
      }), /*#__PURE__*/_jsx(HelpIcon, {
        marginInlineStart: "100",
        title: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          children: [/*#__PURE__*/_jsx("b", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "repOverviewTable.headers.tooltips.calculationHeader"
            })
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: getCoverageHeaderTooltip(portalConfig, groupingType === FORECAST_CATEGORY)
          })]
        })
      })]
    }, DefaultTableColumns.COVERAGE);
  };
  const renderCoverageRatioHeader = () => {
    return /*#__PURE__*/_jsxs(SortTH, {
      align: "right",
      columnKey: DefaultTableColumns.COVERAGE_RATIO,
      onSortChange: setSort,
      sort: sort,
      children: [/*#__PURE__*/_jsx(UITruncateString, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "repOverviewTable.headers.coverageHeader"
        })
      }), /*#__PURE__*/_jsx(HelpIcon, {
        marginInlineStart: "100",
        title: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          children: [/*#__PURE__*/_jsx("b", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "repOverviewTable.headers.tooltips.calculationHeader"
            })
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: portalConfig === ForecastAmountSettingOptions.WEIGHTED_AMOUNT || portalConfig === ForecastAmountSettingOptions.FORECAST_AMOUNT ? 'repOverviewTable.headers.tooltips.weightedPipelineCoverage' : 'repOverviewTable.headers.tooltips.totalPipelineCoverage'
          })]
        })
      })]
    }, DefaultTableColumns.COVERAGE_RATIO);
  };
  const renderDealRollupHeader = groupingId => {
    const dealRollupGrouping = getDealGrouping({
      groupingId,
      dealGroupings: dealRollupGroupings
    });
    if (!dealRollupGrouping) {
      return null;
    }
    const label = dealRollupGrouping.label;

    // If we are in the standard grouping (forecast category), check for tooltips
    const tooltipMessage = groupingType === FORECAST_CATEGORY ? getDealGroupingHeaderTooltip(groupingId) : null;
    const title = tooltipMessage ? /*#__PURE__*/_jsx(HelpIcon, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: tooltipMessage
      }),
      children: propertyLabelTranslator(label)
    }) : propertyLabelTranslator(label);
    return /*#__PURE__*/_jsx(SortTH, {
      align: "right",
      columnKey: `${groupingId}${DEAL_GROUPING_SUFFIX}`,
      onSortChange: setSort,
      truncate: true,
      sort: sort,
      children: title
    }, `${groupingId}${DEAL_GROUPING_SUFFIX}`);
  };
  const keyToHeaderRendererMap = {
    [DefaultTableColumns.ATTAINMENT]: renderAttainmentHeader,
    [DefaultTableColumns.COVERAGE]: renderCoverageHeader,
    [DefaultTableColumns.COVERAGE_RATIO]: renderCoverageRatioHeader,
    [DefaultTableColumns.FORECAST]: renderForecastHeader,
    [DefaultTableColumns.NAME]: renderNameHeader
  };
  return /*#__PURE__*/_jsx(HeaderRow, {
    children: columns.map(column => {
      const renderHeader = keyToHeaderRendererMap[column.value];
      return renderHeader !== undefined && column.internal ? renderHeader() : renderDealRollupHeader(column.value);
    })
  });
};
export default RepOverviewTableHeaders;